import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import useConfirmPurchase from '../lib/useConfirmPurchase';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';
import RichText from '../components/RichText';
import { priceLabel, PracticeCardView } from '../components/cards';
import RelatedStrips from '../components/RelatedStrips';
import ProgramHero from '../components/ProgramHero';
import '../styles/learn.css';

// Header atop the course detail + preview — same banner as Events / Groups /
// Practices (ProgramHero): cover color as the background, cover image as the
// tile, text light or dark to suit the color.
function CourseCoverBanner({ course }) {
  return (
    <ProgramHero kind="Course" name={course?.title || ''}
      iconSrc={course?.coverImageUrl ? api.iconUrl(course.coverImageUrl) : null}
      coverColor={course?.coverColor}
      iconColor={course?.iconColor}
      sub={course?.instructorUsername ? <>Instructor: <b>@{course.instructorUsername}</b></> : null} />
  );
}

// Read-only render of a course's title + summary + lesson list, as it appears
// in the reader. Used by the publisher's Preview modal (no enroll/progress/
// navigation — just the content the way a learner sees it).
export function CoursePreviewBody({ course }) {
  const lessons = course?.lessons || [];
  return (
    <>
      <CourseCoverBanner course={course} />
      {course?.summary && <div className="section"><RichText html={course.summary} /></div>}
      <Section title="Lessons">
        {lessons.length === 0 ? <Empty>No lessons yet.</Empty> : lessons.map((l) => (
          <div key={l.id} className="lesson-row" style={{ cursor: 'default' }}>
            <div className={`lesson-check${l.completed ? ' done' : ''}`}>{l.completed ? '✓' : ''}</div>
            <div style={{ flex: 1 }}>
              <div className="li-title">{l.title} {l.tier === 'PRO' && <span className="badge pro" style={{ marginLeft: 6 }}>PRO</span>}</div>
              <div className="li-sub">{l.locked
                ? (l.lockReason === 'PREREQUISITE' ? '🔒 Complete the earlier lessons to unlock' : '🔒 Enroll to unlock')
                : (l.subtitle || l.primaryType)}</div>
            </div>
          </div>
        ))}
      </Section>
    </>
  );
}

export default function LearnCoursePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fromHome = useLocation().state?.from === 'home';
  const [params, setParams] = useSearchParams();
  const { loading, error, data, reload } = useAsync(() => api.getLearnCourse(id), [id]);
  useConfirmPurchase(reload);  // grant now on return from Stripe; the poll below then sees it
  const [enrolling, setEnrolling] = useState(false);
  const checkoutStatus = params.get('checkout'); // 'success' | 'cancel' | null
  const [confirming, setConfirming] = useState(checkoutStatus === 'success');
  const pollRef = useRef(null);

  // After a Stripe redirect the webhook may not have landed yet, so poll the
  // course until the enrollment shows up (source of truth is the webhook, not
  // this redirect). Give up after ~20s and let the user reload.
  useEffect(() => {
    if (checkoutStatus !== 'success') return;
    if (data?.enrolled || data?.canAccess) { setConfirming(false); return; }
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try { const fresh = await api.getLearnCourse(id); if (fresh?.enrolled || fresh?.canAccess) { clearInterval(pollRef.current); setConfirming(false); reload(); } }
      catch { /* keep trying */ }
      if (tries >= 10) { clearInterval(pollRef.current); setConfirming(false); }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, data?.enrolled, data?.canAccess]);

  if (loading) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorNote error={error} onRetry={reload} /></div>;

  const enroll = async () => {
    setEnrolling(true);
    try { await api.learnEnroll(id); reload(); }
    catch (e) { alert(e.message); }
    finally { setEnrolling(false); }
  };

  const buy = async () => {
    setEnrolling(true);
    try { const { url } = await api.learnCheckout(id); if (url) window.location.assign(url); }
    catch (e) { alert(e.message); setEnrolling(false); }
  };

  const dismissBanner = () => { const p = new URLSearchParams(params); p.delete('checkout'); p.delete('session_id'); setParams(p, { replace: true }); };

  const lessons = data.lessons;
  const total = lessons.length;
  const done = lessons.filter((l) => l.completed).length;
  const pct = total ? Math.round((done / total) * 100) : 0;
  // First unlocked, not-yet-completed lesson — else first unlocked lesson.
  const next = lessons.find((l) => !l.locked && !l.completed) || lessons.find((l) => !l.locked);

  return (
    <div className="page">
      <PageHead
        breadcrumb={fromHome
          ? <a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>My Courses</a>
          : <a onClick={() => navigate('/courses')} style={{ cursor: 'pointer' }}>Courses</a>}
        title=""
        actions={data.enrolled && next && (
          <button className="btn btn-primary" onClick={() => navigate(`/learn/lesson/${next.id}`)}>
            {done > 0 && done < total ? 'Continue' : 'Start'}
          </button>
        )}
      />

      <CourseCoverBanner course={data} />
      {/* 2026-10-16 — same "you're in" pill as groups / events. */}
      {data.enrolled && (
        <div style={{ margin: '0 0 12px' }}>
          <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)', fontWeight: 700 }}>✓ You're enrolled</span>
        </div>
      )}

      {checkoutStatus === 'success' && (data.enrolled || data.canAccess) && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}><strong>You're enrolled.</strong> Payment received — every lesson is unlocked.</div>
          <button className="btn" onClick={dismissBanner}>Dismiss</button>
        </div>
      )}
      {checkoutStatus === 'success' && confirming && !(data.enrolled || data.canAccess) && (
        <div className="section" style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, color: 'var(--muted)' }}>
          Confirming your payment… this can take a few seconds.
        </div>
      )}
      {checkoutStatus === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismissBanner}>Dismiss</button>
        </div>
      )}

      {data.summary && (
        <div className="section"><RichText html={data.summary} /></div>
      )}

      {/* Show the Enroll / Buy panel until the caller is actually enrolled.
          canAccess is true for free courses even pre-enroll, so gating on
          it hid the Enroll button entirely. design/024 tightens this. */}
      {!data.enrolled && (
        <Section>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {data.paid ? `${priceLabel(data)} — one-time` : 'Free course'}
              </div>
              <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                {data.paid
                  ? 'Purchase to enroll and unlock every lesson.'
                  : 'Enroll to unlock every lesson and track your progress.'}
              </div>
            </div>
            {data.paid ? (
              <button className="btn btn-primary" disabled={enrolling} onClick={buy}>
                {enrolling ? 'Redirecting…' : `Buy — ${priceLabel(data)}`}
              </button>
            ) : (
              <button className="btn btn-primary" disabled={enrolling} onClick={enroll}>
                {enrolling ? 'Enrolling…' : 'Enroll — free'}
              </button>
            )}
          </div>
        </Section>
      )}

      {total > 0 && (
        <div className="section" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: 'var(--green)' }} />
            </div>
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{done} of {total} complete</div>
        </div>
      )}

      <Section title="Lessons">
        {data.lessons.length === 0 ? <Empty>No lessons yet.</Empty> : data.lessons.map((l) => {
          // Gate on enrollment (design/024) — even canAccess-only viewers
          // (free courses) must Enroll before they can open a lesson.
          const gated = !data.enrolled || l.locked;
          const subtitle = gated
            ? (l.locked && l.lockReason === 'PREREQUISITE'
                ? '🔒 Complete the earlier lessons to unlock'
                : '🔒 Enroll to unlock')
            : (l.subtitle || l.primaryType);
          return (
            <div
              key={l.id}
              className="lesson-row"
              onClick={() => { if (!gated) navigate(`/learn/lesson/${l.id}`); }}
              style={gated ? { cursor: 'default' } : undefined}
            >
              <div className={`lesson-check${l.completed ? ' done' : ''}`}>{l.completed ? '✓' : ''}</div>
              <div style={{ flex: 1 }}>
                <div className="li-title">{l.title} {l.tier === 'PRO' && <span className="badge pro" style={{ marginLeft: 6 }}>PRO</span>}</div>
                <div className="li-sub">{subtitle}</div>
              </div>
              {!gated && <span style={{ color: 'var(--muted-2)' }}>›</span>}
            </div>
          );
        })}
      </Section>

      <RelatedStrips sourceKind="course" sourceId={id} />
    </div>
  );
}
